 
import { Rule } from "@fairfleet/geotab";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import WarningIcon from "@mui/icons-material/Warning";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import LinearProgress from "@mui/material/LinearProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useQueryClient } from "@tanstack/react-query";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DiagnosticRef,
  extractDiagnosticRefs,
  isKnownDiagnosticId,
  prepareRuleForImport,
  replaceDiagnosticId,
} from "../utils/prepareRuleForImport";
import DiagnosticResolverDialog from "./DiagnosticResolverDialog";
import { RulesGrid } from "./RulesGrid";
import SearchField from "@/components/filter/SearchField";
import { useGeotabClient } from "@/components/GeotabClientContext";
import {
  RULE_PRESETS,
  RULE_TEMPLATES,
} from "@/constants/ruleTemplates";
import { useRulesByName } from "@/hooks/rules/useRulesByName";
import { useDebounced } from "@/hooks/useDebounced";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RuleStatus {
  rule: Record<string, any>;
  name: string;
  exists: boolean;
  missingDiagnostics: DiagnosticRef[];
  status: "ready" | "exists" | "warning" | "checking";
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function RuleBootstrapManager() {
  const { t } = useTranslation();
  const { geotabClient } = useGeotabClient();
  const queryClient = useQueryClient();

  const [selectedPreset, setSelectedPreset] = useState(RULE_PRESETS[0]?.id ?? "");
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<Set<string>>(
    () => new Set(RULE_PRESETS[0]?.templateIds ?? []),
  );
  const [importing, setImporting] = useState(false);
  const [importedCount, setImportedCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Resolver dialog state
  const [resolverOpen, setResolverOpen] = useState(false);
  const [resolverRuleName, setResolverRuleName] = useState("");
  const [resolverDiagnostics, setResolverDiagnostics] = useState<DiagnosticRef[]>([]);
  const [resolverCallback, setResolverCallback] = useState<{
    resolve: (result: Map<string, string> | null) => void;
  } | null>(null);

  // File import state
  const [fileImportInProgress, setFileImportInProgress] = useState(false);

  // Existing rules — used for both duplicate check and the rule manager grid
  const [search, setSearch] = useState("");
  const searchDebounced = useDebounced(search, 300);
  const { data: allRulesData, isLoading: rulesLoading } = useRulesByName(["%"], "Custom");
  const { data: searchRulesData, isLoading: searchLoading } = useRulesByName(
    ["%" + searchDebounced + "%"],
    "Custom",
  );
  const allRules = useMemo(() => allRulesData?.[0] ?? [], [allRulesData]);
  const searchRules = useMemo(() => searchRulesData?.[0] ?? [], [searchRulesData]);
  const existingRuleNames = useMemo(
    () => new Set(allRules.map((r: Rule) => r.name)),
    [allRules],
  );

  // Rule manager grid selection + export
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const selectedRulesForExport = useMemo(
    () => searchRules.filter((rule: Rule) => selectedIds.includes(rule.id)),
    [selectedIds, searchRules],
  );

  const handleExportSelected = () => {
    const blob = new Blob([JSON.stringify(selectedRulesForExport, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "rules.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  // Collect rules from selected templates
  const selectedRules = useMemo(() => {
    const templates = RULE_TEMPLATES.filter(t => selectedTemplateIds.has(t.id));
    return templates.flatMap(t => t.rules);
  }, [selectedTemplateIds]);

  // Check diagnostic availability for each rule
  const [ruleStatuses, setRuleStatuses] = useState<RuleStatus[]>([]);
  const [checking, setChecking] = useState(false);

  const checkDiagnostics = useCallback(async () => {
    if (selectedRules.length === 0) {
      setRuleStatuses([]);
      return;
    }
    setChecking(true);

    const statuses: RuleStatus[] = [];
    for (const rule of selectedRules) {
      const name = rule.name as string;
      const exists = existingRuleNames.has(name);

      if (exists) {
        statuses.push({ rule, name, exists: true, missingDiagnostics: [], status: "exists" });
        continue;
      }

      const allRefs = extractDiagnosticRefs(rule);
      const nonKnownRefs = allRefs.filter(r => !isKnownDiagnosticId(r.id));
      const uniqueNonKnown = Array.from(
        new Map(nonKnownRefs.map(r => [r.id, r])).values(),
      );

      const missing: DiagnosticRef[] = [];
      for (const ref of uniqueNonKnown) {
        try {
          const resp = await geotabClient.get("Diagnostic", { id: ref.id });
          if (!resp || resp.length === 0) missing.push(ref);
        } catch {
          missing.push(ref);
        }
      }

      statuses.push({
        rule,
        name,
        exists: false,
        missingDiagnostics: missing,
        status: missing.length > 0 ? "warning" : "ready",
      });
    }

    setRuleStatuses(statuses);
    setChecking(false);
  }, [selectedRules, existingRuleNames, geotabClient]);

  useEffect(() => {
    if (!rulesLoading) {
      checkDiagnostics();
    }
  }, [rulesLoading, checkDiagnostics]);

  // Preset / template selection
  const handlePresetChange = useCallback((presetId: string) => {
    setSelectedPreset(presetId);
    const preset = RULE_PRESETS.find(p => p.id === presetId);
    if (preset) {
      setSelectedTemplateIds(new Set(preset.templateIds));
    }
  }, []);

  const handleTemplateToggle = useCallback((templateId: string) => {
    setSelectedTemplateIds(prev => {
      const next = new Set(prev);
      if (next.has(templateId)) next.delete(templateId);
      else next.add(templateId);
      return next;
    });
    setSelectedPreset("");
  }, []);

  // Import logic
  const readyRules = useMemo(() => ruleStatuses.filter(s => s.status === "ready"), [ruleStatuses]);
  const warningRules = useMemo(() => ruleStatuses.filter(s => s.status === "warning"), [ruleStatuses]);
  const existsRules = useMemo(() => ruleStatuses.filter(s => s.status === "exists"), [ruleStatuses]);

  const handleImportAll = async () => {
    setImporting(true);
    setImportedCount(0);
    setError(null);
    setSuccessMessage(null);

    try {
      const toImport = [...ruleStatuses];
      let imported = 0;

      for (const status of toImport) {
        if (status.exists) continue;

        let ruleData = status.rule;
        if (status.missingDiagnostics.length > 0) {
          const resolutions = await promptDiagnosticResolution(
            status.name,
            status.missingDiagnostics,
          );
          if (!resolutions) continue;
          for (const [oldId, newId] of resolutions) {
            ruleData = replaceDiagnosticId(ruleData, oldId, newId);
          }
        }

        const prepared = prepareRuleForImport(ruleData);
        await geotabClient.call("Add", { typeName: "Rule", entity: prepared });
        imported++;
        setImportedCount(imported);
      }

      await queryClient.invalidateQueries({ queryKey: ["rule"] });
      setSuccessMessage(t("admin.rules.importSuccess", { count: imported }));
      checkDiagnostics();
    } catch (e) {
      setError(e instanceof Error ? e.message : t("admin.rules.importError"));
    } finally {
      setImporting(false);
    }
  };

  const promptDiagnosticResolution = (
    ruleName: string,
    diagnostics: DiagnosticRef[],
  ): Promise<Map<string, string> | null> => {
    return new Promise(resolve => {
      setResolverRuleName(ruleName);
      setResolverDiagnostics(diagnostics);
      setResolverCallback({ resolve });
      setResolverOpen(true);
    });
  };

  const handleResolverResolve = (resolutions: Map<string, string>) => {
    setResolverOpen(false);
    resolverCallback?.resolve(resolutions);
  };

  const handleResolverSkip = () => {
    setResolverOpen(false);
    resolverCallback?.resolve(null);
  };

  // File import
  const handleFileImport = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = "";

    const reader = new FileReader();
    reader.onload = async e => {
      try {
        const importedRules: Rule[] = JSON.parse(e.target?.result as string);
        setFileImportInProgress(true);
        setError(null);
        let imported = 0;

        for (const rule of importedRules) {
          if (existingRuleNames.has(rule.name)) {
            setError(prev =>
              (prev ? prev + "\n" : "") + t("admin.rules.ruleExists", { name: rule.name }),
            );
            continue;
          }

          const refs = extractDiagnosticRefs(rule as Record<string, any>);
          const nonKnown = refs.filter(r => !isKnownDiagnosticId(r.id));
          const uniqueNonKnown = Array.from(new Map(nonKnown.map(r => [r.id, r])).values());

          const missing: DiagnosticRef[] = [];
          for (const ref of uniqueNonKnown) {
            try {
              const resp = await geotabClient.get("Diagnostic", { id: ref.id });
              if (!resp || resp.length === 0) missing.push(ref);
            } catch {
              missing.push(ref);
            }
          }

          let ruleData: Record<string, any> = rule;
          if (missing.length > 0) {
            const resolutions = await promptDiagnosticResolution(rule.name, missing);
            if (!resolutions) continue;
            for (const [oldId, newId] of resolutions) {
              ruleData = replaceDiagnosticId(ruleData, oldId, newId);
            }
          }

          const prepared = prepareRuleForImport(ruleData);
          await geotabClient.call("Add", { typeName: "Rule", entity: prepared });
          imported++;
        }

        await queryClient.invalidateQueries({ queryKey: ["rule"] });
        if (imported > 0) {
          setSuccessMessage(t("admin.rules.importSuccess", { count: imported }));
        }
        checkDiagnostics();
      } catch (err) {
        setError(err instanceof Error ? err.message : t("admin.rules.importError"));
      } finally {
        setFileImportInProgress(false);
      }
    };
    reader.readAsText(file);
  };

  if (rulesLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const importableCount = readyRules.length + warningRules.length;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ whiteSpace: "pre-line" }}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* ================================================================= */}
      {/* Bootstrap from templates                                           */}
      {/* ================================================================= */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight={500}>
            {t("admin.rules.bootstrapTitle")}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {/* Preset + import buttons */}
          <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center", flexWrap: "wrap" }}>
            <TextField
              select
              size="small"
              label={t("admin.rules.preset")}
              value={selectedPreset}
              onChange={e => handlePresetChange(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">
                <em>{t("admin.groups.customSelection")}</em>
              </MenuItem>
              {RULE_PRESETS.map(p => (
                <MenuItem key={p.id} value={p.id}>
                  {p.label}
                </MenuItem>
              ))}
            </TextField>

            <Button
              variant="contained"
              size="small"
              onClick={handleImportAll}
              disabled={importing || importableCount === 0 || checking}
            >
              {importing
                ? t("admin.rules.importing")
                : importableCount === 0
                  ? t("admin.rules.allExist")
                  : t("admin.rules.importCount", { count: importableCount })}
            </Button>

            <Button
              variant="outlined"
              size="small"
              component="label"
              disabled={fileImportInProgress}
            >
              {fileImportInProgress ? t("admin.rules.importing") : t("admin.rules.importFile")}
              <input type="file" accept="application/json" hidden onChange={handleFileImport} />
            </Button>
          </Box>

          {/* Template checkboxes */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
            {RULE_TEMPLATES.map(tmpl => (
              <FormControlLabel
                key={tmpl.id}
                control={
                  <Checkbox
                    checked={selectedTemplateIds.has(tmpl.id)}
                    onChange={() => handleTemplateToggle(tmpl.id)}
                    size="small"
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {tmpl.label}
                    <Chip label={`${tmpl.rules.length}`} size="small" variant="outlined" />
                  </Box>
                }
              />
            ))}
          </Box>

          {/* Progress bar */}
          {importing && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress
                variant="determinate"
                value={importableCount > 0 ? (importedCount / importableCount) * 100 : 0}
              />
              <Typography variant="caption" sx={{ mt: 0.5 }}>
                {t("admin.groups.progress", { created: importedCount, total: importableCount })}
              </Typography>
            </Box>
          )}

          {/* Summary chips */}
          {ruleStatuses.length > 0 && !checking && (
            <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
              <Chip
                icon={<RadioButtonUncheckedIcon />}
                label={t("admin.rules.readyCount", { count: readyRules.length })}
                size="small"
                color="primary"
                variant="outlined"
              />
              <Chip
                icon={<CheckCircleIcon />}
                label={t("admin.rules.existsCount", { count: existsRules.length })}
                size="small"
                color="success"
                variant="outlined"
              />
              {warningRules.length > 0 && (
                <Chip
                  icon={<WarningIcon />}
                  label={t("admin.rules.warningCount", { count: warningRules.length })}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              )}
            </Box>
          )}

          {/* Template rule list */}
          <Paper variant="outlined" sx={{ maxHeight: 300, overflow: "auto" }}>
            {checking ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 3 }}>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                <Typography variant="body2">{t("admin.rules.checkingDiagnostics")}</Typography>
              </Box>
            ) : (
              <List disablePadding dense>
                {ruleStatuses.length > 0 ? (
                  ruleStatuses.map((status, idx) => (
                    <ListItem key={`${status.name}-${idx}`} divider dense>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {status.status === "exists" ? (
                          <Tooltip title={t("admin.rules.alreadyExists")}>
                            <CheckCircleIcon fontSize="small" color="success" />
                          </Tooltip>
                        ) : status.status === "warning" ? (
                          <Tooltip
                            title={t("admin.rules.missingDiagnosticsTooltip", {
                              count: status.missingDiagnostics.length,
                            })}
                          >
                            <WarningIcon fontSize="small" color="warning" />
                          </Tooltip>
                        ) : (
                          <Tooltip title={t("admin.groups.willCreate")}>
                            <RadioButtonUncheckedIcon fontSize="small" color="primary" />
                          </Tooltip>
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={status.name}
                        secondary={
                          status.status === "warning"
                            ? status.missingDiagnostics.map(d => d.id).join(", ")
                            : status.rule.comment || undefined
                        }
                        slotProps={{
                          secondary: {
                            color: status.status === "warning" ? "warning.main" : undefined,
                            sx: {
                              fontFamily: status.status === "warning" ? "monospace" : undefined,
                              fontSize: status.status === "warning" ? "0.75rem" : undefined,
                            },
                          },
                        }}
                      />
                    </ListItem>
                  ))
                ) : (
                  <ListItem>
                    <ListItemText
                      primary={t("admin.groups.noTemplatesSelected")}
                      slotProps={{ primary: { color: "text.secondary" } }}
                    />
                  </ListItem>
                )}
              </List>
            )}
          </Paper>
        </AccordionDetails>
      </Accordion>

      {/* ================================================================= */}
      {/* Existing rules browser                                             */}
      {/* ================================================================= */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="subtitle1" fontWeight={500}>
              {t("admin.rules.existingRules")}
            </Typography>
            <Chip label={allRules.length} size="small" variant="outlined" />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
            <SearchField
              value={search}
              setValue={setSearch}
              title={t("diagnosticEdit.ruleTitle")}
              placeholder={t("diagnosticEdit.ruleInptPlaceholder")}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportSelected}
              disabled={selectedIds.length === 0}
              sx={{ flexShrink: 0 }}
            >
              {t("admin.rules.exportSelected")}
            </Button>
          </Box>

          <Paper variant="outlined" sx={{ height: 350 }}>
            <RulesGrid
              isLoading={searchLoading}
              data={searchRules}
              setSelectedIds={setSelectedIds}
            />
          </Paper>
        </AccordionDetails>
      </Accordion>

      {/* Diagnostic resolver dialog */}
      <DiagnosticResolverDialog
        open={resolverOpen}
        ruleName={resolverRuleName}
        missingDiagnostics={resolverDiagnostics}
        onResolve={handleResolverResolve}
        onSkip={handleResolverSkip}
      />
    </Box>
  );
}
